import { useGetMailServerSchemes } from '@/containers/pages/filials/edit/components/tabs/mail-servers/hooks/useGetMailServerSchemes';
import { IMailServer } from '@/services/api/emailServerApiArea/types';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormErrors, useForm, UseFormReturnType } from '@mantine/form';
import { getValidateErrors } from '@/lib/utils/validation';
import { showNotification } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { emailServerApiArea } from '@/services/api/emailServerApiArea';

interface Params {
  CustomerId: number;
  handleChange?: () => void;
  mailServer: IMailServer;
  opened: boolean;
  handleClose: () => void;
  isEditing: boolean;
}

interface Return {
  form: UseFormReturnType<IMailServer>;
  isFetching: boolean;
  isOnIntegration: boolean;

  setIsOnIntegration: Dispatch<SetStateAction<boolean>>;
  handleSubmit(data: IMailServer): void;
  modalClose(): void;
}

export const useServerModalForm = (params: Params): Return => {
  const { t } = useTranslation();

  const { mailServer, CustomerId, handleClose, handleChange } = params;
  const [isOnIntegration, setIsOnIntegration] = useState<boolean>(!!mailServer?.Integration);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const { validateScheme } = useGetMailServerSchemes(!!mailServer?.Integration?.Ssh?.HasKey);

  const form = useForm<IMailServer>({
    initialValues: {
      ...structuredClone(mailServer),
      CustomerId
    },
    validateInputOnChange: true,
    validate: (values: IMailServer): FormErrors => getValidateErrors(validateScheme, values)
  });

  const modalClose = () => {
    handleClose();
    form.reset();
  };

  const handleSubmit = (values: IMailServer) => {
    const preparedValues = emailServerApiArea().PrepareValues(values);

    setIsFetching(true);
    emailServerApiArea()
      .EmailServerSave(preparedValues)
      .then(() => {
        showNotification({
          title: t('servers.createSuccess'),
          message: '',
          autoClose: true,
          color: 'green',
          style: { zIndex: 1003 }
        });

        handleChange?.();
        handleClose();
      })
      .catch((err) => {
        showNotification({
          title: `${(err as any)?.error?.data}`,
          message: '',
          autoClose: true,
          color: 'red',
          style: { zIndex: 1003 }
        });

        throw err;
      })
      .finally(() => setIsFetching(false));
  };

  return {
    form,
    isFetching,
    isOnIntegration,
    modalClose,
    handleSubmit,
    setIsOnIntegration
  };
};
